import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const STEPS = [
  {
    key: 'sleep_schedule',
    question: "What's your sleep schedule?",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
    ),
    options: [
      { value: 'early_bird', label: 'Early Bird', desc: 'Up by 7am, in bed by 10pm' },
      { value: 'night_owl', label: 'Night Owl', desc: 'Up late, sleep in most days' },
      { value: 'flexible', label: 'Flexible', desc: 'No set schedule, varies by day' },
    ],
  },
  {
    key: 'cleanliness',
    question: 'How clean do you keep your space?',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    options: [
      { value: 'very_clean', label: 'Very Clean', desc: 'Dishes done immediately, floors vacuumed weekly' },
      { value: 'average', label: 'Average', desc: 'Tidy but not obsessive' },
      { value: 'relaxed', label: 'Relaxed', desc: "Clutter doesn't bother me" },
    ],
  },
  {
    key: 'noise_preference',
    question: "What's your noise preference at home?",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
      </svg>
    ),
    options: [
      { value: 'quiet', label: 'Quiet', desc: 'I prefer silence, especially when studying' },
      { value: 'moderate', label: 'Moderate', desc: 'Some background noise is totally fine' },
      { value: 'social', label: 'Social', desc: 'Music, TV, people around — love it' },
      { value: 'depends', label: 'Depends', desc: 'Quiet during the week, louder on weekends' },
    ],
  },
  {
    key: 'guest_preference',
    question: 'How often do you have guests over?',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    options: [
      { value: 'rarely', label: 'Rarely', desc: 'My place is my private space' },
      { value: 'sometimes', label: 'Sometimes', desc: 'Occasionally friends visit' },
      { value: 'often', label: 'Often', desc: 'I love having people over' },
    ],
  },
  {
    key: 'study_habits',
    question: 'Where do you study?',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
      </svg>
    ),
    options: [
      { value: 'at_home', label: 'At Home', desc: 'I need a quiet home to study in' },
      { value: 'elsewhere', label: 'Library / Café', desc: 'I study outside the apartment' },
    ],
  },
  {
    key: 'sports',
    question: 'What sports are you into? (pick all that apply)',
    multi: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l4.24 4.24"/><path d="M14.83 9.17l4.24-4.24"/><path d="M14.83 14.83l4.24 4.24"/><path d="M9.17 14.83l-4.24 4.24"/>
      </svg>
    ),
    options: [
      { value: 'basketball', label: 'Basketball' },
      { value: 'football', label: 'Football' },
      { value: 'soccer', label: 'Soccer' },
      { value: 'baseball', label: 'Baseball' },
      { value: 'tennis', label: 'Tennis' },
      { value: 'golf', label: 'Golf' },
      { value: 'running', label: 'Running' },
      { value: 'swimming', label: 'Swimming' },
      { value: 'volleyball', label: 'Volleyball' },
      { value: 'hockey', label: 'Hockey' },
      { value: 'cycling', label: 'Cycling' },
      { value: 'gym', label: 'Gym / Lifting' },
      { value: 'no_sports', label: 'Not into sports' },
    ],
  },
  {
    key: 'interests',
    question: 'What do you do in your free time? (pick all that apply)',
    multi: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
    options: [
      { value: 'gaming', label: 'Gaming' },
      { value: 'reading', label: 'Reading' },
      { value: 'music', label: 'Music' },
      { value: 'cooking', label: 'Cooking' },
      { value: 'hiking', label: 'Hiking / Outdoors' },
      { value: 'art', label: 'Art / Drawing' },
      { value: 'movies', label: 'Movies / TV' },
      { value: 'photography', label: 'Photography' },
      { value: 'traveling', label: 'Traveling' },
      { value: 'fitness', label: 'Fitness' },
      { value: 'coding', label: 'Coding' },
      { value: 'board_games', label: 'Board Games' },
    ],
  },
  {
    key: 'college_year',
    question: 'What year are you in school?',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    ),
    options: [
      { value: 'freshman', label: 'Freshman', desc: '1st year' },
      { value: 'sophomore', label: 'Sophomore', desc: '2nd year' },
      { value: 'junior', label: 'Junior', desc: '3rd year' },
      { value: 'senior', label: 'Senior', desc: '4th year' },
      { value: 'graduate', label: 'Graduate Student', desc: "Master's or PhD" },
      { value: 'not_in_school', label: 'Not in School', desc: 'Working or taking time off' },
    ],
  },
  {
    key: 'preferred_gender',
    question: 'What gender roommate are you looking for?',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <circle cx="12" cy="8" r="4"/><path d="M12 12v8"/><path d="M9 19h6"/>
      </svg>
    ),
    options: [
      { value: 'male', label: 'Male', desc: 'Looking for a male roommate' },
      { value: 'female', label: 'Female', desc: 'Looking for a female roommate' },
      { value: 'no_preference', label: 'No Preference', desc: "Gender doesn't matter to me" },
    ],
  },
];

export default function Quiz({ onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [budget, setBudget] = useState({ min: '', max: '' });
  const [moveIn, setMoveIn] = useState('');
  const [hometown, setHometown] = useState('');
  const [extracurriculars, setExtracurriculars] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [_existing, setExisting] = useState(null); // eslint-disable-line no-unused-vars

  useEffect(() => {
    api.get('/roommates/quiz').then(res => {
      if (res.data) {
        setExisting(res.data);
        const a = {};
        STEPS.forEach(s => {
          if (res.data[s.key]) {
            if (s.multi) {
              try { a[s.key] = JSON.parse(res.data[s.key]); } catch { a[s.key] = []; }
            } else {
              a[s.key] = res.data[s.key];
            }
          }
        });
        setAnswers(a);
        setBudget({ min: res.data.budget_min || '', max: res.data.budget_max || '' });
        if (res.data.move_in_date) setMoveIn(res.data.move_in_date.split('T')[0]);
        if (res.data.hometown) setHometown(res.data.hometown);
        if (res.data.extracurriculars) setExtracurriculars(res.data.extracurriculars);
      }
    }).catch(() => {});
  }, []);

  const totalSteps = STEPS.length + 2; // +1 details, +1 photo
  const progress = (step / totalSteps) * 100;
  const currentStep = STEPS[step];

  // Single-select: pick and auto-advance
  const select = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    setTimeout(() => setStep(prev => prev + 1), 300);
  };

  // Multi-select: toggle item in/out of array
  const toggleMulti = (key, value) => {
    setAnswers(prev => {
      const current = prev[key] || [];
      const isSelected = current.includes(value);
      // If toggling "not into sports", clear others; if selecting another, clear "no_sports"
      if (value === 'no_sports') {
        return { ...prev, [key]: isSelected ? [] : ['no_sports'] };
      }
      const filtered = current.filter(v => v !== 'no_sports');
      return {
        ...prev,
        [key]: isSelected ? filtered.filter(v => v !== value) : [...filtered, value],
      };
    });
  };

  const isMultiSelected = (key, value) => {
    const arr = answers[key] || [];
    return arr.includes(value);
  };

  const handlePhotoChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setPhotoFiles(prev => {
      const combined = [...prev, ...newFiles].slice(0, 8);
      setPhotoPreviews(combined.map(f => URL.createObjectURL(f)));
      return combined;
    });
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      setPhotoPreviews(updated.map(f => URL.createObjectURL(f)));
      return updated;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/roommates/quiz', {
        ...answers,
        interests: answers.interests || [],
        sports: answers.sports || [],
        budget_min: parseInt(budget.min) || null,
        budget_max: parseInt(budget.max) || null,
        move_in_date: moveIn || null,
        hometown: hometown || null,
        extracurriculars: extracurriculars || null,
      });
      if (photoFiles.length > 0) {
        const fd = new FormData();
        photoFiles.forEach(f => fd.append('photos', f));
        await api.post('/profile/photos', fd);
      }
      onComplete();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black text-[#1B1B1B] mb-2">Compatibility Quiz</h1>
        <p className="nav-link text-[#777777]">Help us find your perfect roommate match</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-[#AAAAAA] mb-2">
          <span>Step {step + 1} of {totalSteps}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-[#EEEEEE] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C8A800] rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {step < STEPS.length ? (
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-[#C8A800]" style={{ backgroundColor: 'rgba(200,168,0,0.10)' }}>
              {currentStep.icon}
            </div>
            <h2 className="text-xl font-bold text-[#1B1B1B]">{currentStep.question}</h2>
          </div>

          {currentStep.multi ? (
            <>
              <div className="flex flex-wrap gap-2 mb-6">
                {currentStep.options.map(opt => {
                  const selected = isMultiSelected(currentStep.key, opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleMulti(currentStep.key, opt.value)}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 ${
                        selected
                          ? 'border-[#C8A800] bg-[#C8A800] text-white'
                          : 'border-[#E0E0E0] text-[#555555] hover:border-[#C8A800] hover:text-[#C8A800]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-2">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="btn-ghost flex-shrink-0 border border-[#DDDDDD] px-5">
                    Back
                  </button>
                )}
                <button
                  onClick={() => setStep(step + 1)}
                  className="btn-primary flex-1"
                >
                  {(answers[currentStep.key] || []).length === 0 ? 'Skip' : 'Continue'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                {currentStep.options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => select(currentStep.key, opt.value)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-all duration-200 ${
                      answers[currentStep.key] === opt.value
                        ? 'border-[#C8A800] bg-[#C8A800]/8'
                        : 'border-[#E0E0E0] hover:border-[#C8A800] hover:bg-[#C8A800]/5'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 transition-colors ${
                      answers[currentStep.key] === opt.value ? 'border-[#C8A800] bg-[#C8A800]' : 'border-[#CCCCCC]'
                    }`} />
                    <div>
                      <p className="font-semibold text-[#1B1B1B]">{opt.label}</p>
                      {opt.desc && <p className="nav-link text-sm text-[#777777]">{opt.desc}</p>}
                    </div>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-6 nav-link text-[#999999] hover:text-[#555555] text-sm transition-colors">
                  ← Back
                </button>
              )}
            </>
          )}
        </div>
      ) : step === STEPS.length ? (
        // Details screen
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-[#C8A800]" style={{ backgroundColor: 'rgba(200,168,0,0.10)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-[#1B1B1B]">Almost done!</h2>
            <p className="nav-link text-[#777777] mt-1">A few last details to find your best match</p>
          </div>
          <div className="space-y-5">
            <div>
              <label className="label">Hometown</label>
              <input
                className="input"
                placeholder="e.g. Chicago, IL"
                value={hometown}
                onChange={e => setHometown(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Additional Notes <span className="text-[#AAAAAA] font-normal normal-case">(optional)</span></label>
              <textarea
                className="input resize-none"
                rows={3}
                placeholder="Anything else potential roommates should know about you..."
                value={extracurriculars}
                onChange={e => setExtracurriculars(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Ideal Move-in Date</label>
              <input
                type="date"
                className="input"
                value={moveIn}
                onChange={e => setMoveIn(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(step - 1)} className="btn-ghost flex-1 border border-[#DDDDDD]">
                Back
              </button>
              <button onClick={() => setStep(step + 1)} className="btn-primary flex-1">
                Next →
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Photo upload screen
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-[#C8A800]" style={{ backgroundColor: 'rgba(200,168,0,0.10)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-[#1B1B1B]">Add a profile photo</h2>
            <p className="nav-link text-[#777777] mt-1">Help others put a face to your name — you can skip this and add one later from your profile</p>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-4">
            {photoPreviews.map((src, i) => (
              <div key={i} className="relative aspect-square">
                <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover rounded-xl" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-[#1B1B1B] text-white rounded-full flex items-center justify-center text-xs hover:bg-red-500 transition-colors"
                >
                  ✕
                </button>
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 text-[9px] bg-[#C8A800] text-white px-1.5 py-0.5 rounded font-semibold">Profile pic</span>
                )}
              </div>
            ))}
            {photoPreviews.length < 8 && (
              <label className="aspect-square cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoChange} />
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#DDDDDD] hover:border-[#C8A800] transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-[#CCCCCC]">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  <span className="text-[10px] text-[#AAAAAA] nav-link">Add</span>
                </div>
              </label>
            )}
          </div>
          <p className="text-xs text-[#AAAAAA] nav-link text-center mb-2">
            {photoPreviews.length === 0 ? 'Up to 8 photos — first one becomes your profile picture' : `${photoPreviews.length}/8 photos — first one is your profile picture`}
          </p>

          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(step - 1)} className="btn-ghost flex-1 border border-[#DDDDDD]">
              Back
            </button>
            <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : 'Find My Matches →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
